import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Button,
  Row,
  Col,
  Divider,
  message,
  Space,
  Alert,
  Image,
  Empty,
  Tooltip,
  Popconfirm,
  Spin,
} from 'antd';
import { SaveOutlined, StarFilled, StarOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { adminService } from '../../api/services/adminService';
import { vehicleManagementService, type MakeSummary, type ModelSummary } from '../../api/services/vehicleManagementService';

const { TextArea } = Input;
const { Option } = Select;

const FUEL_TYPES = ['PETROL', 'DIESEL', 'ELECTRIC', 'HYBRID', 'LPG', 'OTHER'];
const TRANSMISSIONS = ['MANUAL', 'AUTOMATIC', 'SEMI_AUTOMATIC'];
const BODY_TYPES = ['SEDAN', 'SUV', 'HATCHBACK', 'COUPE', 'WAGON', 'VAN', 'TRUCK', 'CONVERTIBLE', 'OTHER'];
const CONDITIONS = ['NEW', 'USED', 'CERTIFIED_PRE_OWNED'];
const STATUSES = ['DRAFT', 'UNDER_APPROVAL', 'ACTIVE', 'REJECTED', 'SUSPENDED', 'SOLD'];
const DRIVE_TYPES: { value: string; label: string }[] = [
  { value: 'front_wheel_drive', label: 'FWD (Front Wheel Drive)' },
  { value: 'rear_wheel_drive', label: 'RWD (Rear Wheel Drive)' },
  { value: 'all_wheel_drive', label: 'AWD / 4WD (All Wheel Drive)' },
];
const INTERIOR_MATERIALS: { value: string; label: string }[] = [
  { value: 'full_grain_leather', label: 'Full-grain Leather' },
  { value: 'alcantara', label: 'Alcantara' },
  { value: 'partial_leather', label: 'Partial Leather' },
  { value: 'artificial_leather', label: 'Artificial Leather' },
  { value: 'velour', label: 'Velour' },
  { value: 'material', label: 'Material' },
  { value: 'other', label: 'Other' },
];
const INTERIOR_COLORS = ['black', 'beige', 'gray', 'brown', 'red', 'white', 'blue', 'other'];
const CAR_COLORS = ['Black', 'White', 'Silver', 'Gray', 'Beige', 'Brown', 'Red', 'Orange', 'Yellow', 'Blue', 'Green', 'Gold', 'Purple', 'Chrome', 'Other'];
const FEATURES_OPTIONS = [
  'Air Conditioning', 'Bluetooth', 'Navigation', 'Leather Seats',
  'Sunroof', 'Parking Sensors', 'Backup Camera', 'Heated Seats',
  'Apple CarPlay', 'Android Auto', 'Cruise Control', 'Alloy Wheels',
  'Keyless Entry', 'Push Start', 'Adaptive Cruise Control',
];

interface AdminEditListingModalProps {
  visible: boolean;
  listing: any;
  onClose: () => void;
  onSaved: () => void;
}

const AdminEditListingModal: React.FC<AdminEditListingModalProps> = ({
  visible,
  listing,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [makes, setMakes] = useState<MakeSummary[]>([]);
  const [models, setModels] = useState<ModelSummary[]>([]);
  const [makesLoading, setMakesLoading] = useState(false);
  const [modelsLoading, setModelsLoading] = useState(false);

  const [photos, setPhotos] = useState<Array<{ id: string; url: string; isPrimary: boolean; alt: string }>>([]);
  const [photoLoading, setPhotoLoading] = useState<Record<string, boolean>>({});
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (visible && listing) {
      setPhotos(Array.isArray(listing.images) ? listing.images : []);
    }
  }, [visible, listing]);

  const handleSetPrimary = async (photoId: string) => {
    if (!listing?.id) return;
    setPhotoLoading(prev => ({ ...prev, [photoId]: true }));
    try {
      await adminService.setPhotoPrimary(listing.id, photoId);
      setPhotos(prev => prev.map(p => ({ ...p, isPrimary: p.id === photoId })));
    } catch {
      message.error(t('admin.edit_listing.msg_set_primary_failed'));
    } finally {
      setPhotoLoading(prev => ({ ...prev, [photoId]: false }));
    }
  };

  const handleDeletePhoto = async (photoId: string) => {
    if (!listing?.id) return;
    setPhotoLoading(prev => ({ ...prev, [photoId]: true }));
    try {
      await adminService.deletePhoto(listing.id, photoId);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      message.success(t('admin.edit_listing.msg_photo_deleted'));
    } catch {
      message.error(t('admin.edit_listing.msg_delete_photo_failed'));
      setPhotoLoading(prev => ({ ...prev, [photoId]: false }));
    }
  };

  const handleUploadPhotos = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !listing?.id) return;
    e.target.value = '';
    setUploading(true);
    try {
      const result = await adminService.uploadListingPhotos(listing.id, files);
      const updated = await adminService.getListingDetails(listing.id);
      setPhotos(Array.isArray(updated.images) ? updated.images : []);
      message.success(t('admin.edit_listing.msg_photos_uploaded', { count: result.count ?? files.length }));
    } catch (err: any) {
      message.error(err?.response?.data?.message || t('admin.edit_listing.msg_upload_failed'));
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    setMakesLoading(true);
    vehicleManagementService.getMakes()
      .then(setMakes)
      .catch(() => setMakes([]))
      .finally(() => setMakesLoading(false));
  }, []);

  const handleMakeChange = (make: string) => {
    form.setFieldValue('model', undefined);
    setModels([]);
    if (!make) return;
    setModelsLoading(true);
    vehicleManagementService.getModels(make)
      .then(setModels)
      .catch(() => setModels([]))
      .finally(() => setModelsLoading(false));
  };

  useEffect(() => {
    if (visible && listing) {
      if (listing.make) {
        setModelsLoading(true);
        vehicleManagementService.getModels(listing.make)
          .then(setModels)
          .catch(() => setModels([]))
          .finally(() => setModelsLoading(false));
      }
      const currentStatus = typeof listing.status === 'object'
        ? listing.status?.current
        : listing.status;
      const adminNotes = listing.adminNotes ?? listing.admin?.adminNotes ?? '';

      form.setFieldsValue({
        make: listing.make ?? undefined,
        model: listing.model ?? undefined,
        year: listing.year ?? null,
        price: listing.price ? Number(listing.price) : null,
        mileage: listing.mileage ? Number(listing.mileage) : null,
        color: listing.color ?? '',
        fuelType: listing.fuelType ?? undefined,
        transmission: listing.transmission ?? undefined,
        bodyType: listing.bodyType ?? undefined,
        condition: listing.condition ?? undefined,
        description: listing.description ?? '',
        title: listing.title ?? '',
        isNegotiable: listing.isNegotiable ?? true,
        location: listing.location ?? '',
        city: listing.city ?? '',
        country: listing.country ?? 'Albania',
        doors: listing.doors ?? null,
        seats: listing.seats ?? null,
        displacement: listing.displacement ?? '',
        driveType: listing.driveType ?? undefined,
        numberOfOwners: listing.numberOfOwners ?? null,
        interiorColor: listing.equipment?.interiorColor ?? listing.extras?.interiorColor ?? undefined,
        isMetallic: listing.equipment?.isMetallic ?? listing.extras?.isMetallic ?? undefined,
        interiorMaterial: listing.equipment?.interiorMaterial ?? listing.extras?.interiorMaterial ?? undefined,
        features: Array.isArray(listing.features) ? listing.features : [],
        status: currentStatus ?? 'DRAFT',
        adminNotes,
        contactPhone: (listing.contactPhone ?? listing.user?.phone ?? '').replace(/^\+355/, ''),
        displayPhone: listing.displayPhone !== false,
        showWhatsApp: listing.showWhatsApp === true,
      });
    }
  }, [visible, listing, form]);

  const handleSave = async (values: any) => {
    setSaving(true);
    try {
      await adminService.adminUpdateListing(listing.id, {
        make: values.make || undefined,
        model: values.model || undefined,
        year: values.year ?? undefined,
        price: values.price ?? undefined,
        mileage: values.mileage ?? undefined,
        color: values.color || undefined,
        fuelType: values.fuelType || undefined,
        transmission: values.transmission || undefined,
        bodyType: values.bodyType || undefined,
        condition: values.condition || undefined,
        description: values.description || undefined,
        title: values.title || undefined,
        isNegotiable: values.isNegotiable,
        location: values.location || undefined,
        city: values.city || undefined,
        country: values.country || undefined,
        doors: values.doors ?? undefined,
        seats: values.seats ?? undefined,
        displacement: values.displacement || undefined,
        driveType: values.driveType || undefined,
        numberOfOwners: values.numberOfOwners ?? undefined,
        interiorColor: values.interiorColor || undefined,
        isMetallic: values.isMetallic || undefined,
        interiorMaterial: values.interiorMaterial || undefined,
        features: values.features ?? undefined,
        status: values.status || undefined,
        adminNotes: values.adminNotes || undefined,
        contactPhone: values.contactPhone
          ? values.contactPhone.startsWith('+') ? values.contactPhone : `+355${values.contactPhone}`
          : undefined,
        displayPhone: values.displayPhone,
        showWhatsApp: values.showWhatsApp,
      });
      message.success(t('admin.edit_listing.msg_listing_updated'));
      onSaved();
      onClose();
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || t('admin.edit_listing.msg_update_failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={listing
        ? t('admin.edit_listing.title_with', { year: listing.year, make: listing.make, model: listing.model })
        : t('admin.edit_listing.title_fallback')}
      open={visible}
      onCancel={onClose}
      width={900}
      style={{ top: 20 }}
      footer={null}
      destroyOnClose
    >
      <Alert
        type="warning"
        showIcon
        message={t('admin.edit_listing.alert_message')}
        description={t('admin.edit_listing.alert_desc')}
        style={{ marginBottom: 20 }}
      />

      {/* Images */}
      <div style={{ marginBottom: 20 }}>
        <Divider orientation="left" style={{ color: '#fe5a1d', borderColor: '#fe5a1d', marginTop: 0 }}>
          <span>{t('admin.edit_listing.images_section', { count: photos.length })}</span>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={handleUploadPhotos}
          />
          <Button
            size="small"
            icon={<UploadOutlined />}
            loading={uploading}
            onClick={() => fileInputRef.current?.click()}
            style={{ marginLeft: 12 }}
          >
            {t('admin.edit_listing.btn_upload')}
          </Button>
        </Divider>

        {photos.length > 0 ? (
          <Image.PreviewGroup>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {photos.map(img => (
                <Spin key={img.id} spinning={!!photoLoading[img.id]}>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <Image
                        width={100}
                        height={75}
                        src={img.url}
                        alt={img.alt}
                        style={{
                          objectFit: 'cover',
                          borderRadius: 4,
                          border: img.isPrimary ? '2px solid #fe5a1d' : '1px solid #d9d9d9',
                          display: 'block',
                        }}
                        fallback="/images/default-car.jpg"
                      />
                      {img.isPrimary && (
                        <StarFilled style={{
                          position: 'absolute',
                          top: 5,
                          left: 5,
                          color: '#faad14',
                          fontSize: 14,
                          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))',
                          zIndex: 1,
                        }} />
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 4, marginTop: 4, justifyContent: 'center' }}>
                      {!img.isPrimary && (
                        <Tooltip title={t('admin.edit_listing.tooltip_set_primary')}>
                          <Button
                            size="small"
                            type="text"
                            icon={<StarOutlined style={{ color: '#faad14' }} />}
                            onClick={() => handleSetPrimary(img.id)}
                            style={{ padding: '0 4px', height: 22 }}
                          />
                        </Tooltip>
                      )}
                      <Popconfirm
                        title={t('admin.edit_listing.popconfirm_delete_photo')}
                        onConfirm={() => handleDeletePhoto(img.id)}
                        okText={t('admin.edit_listing.btn_delete')}
                        okType="danger"
                        cancelText={t('admin.edit_listing.btn_cancel')}
                      >
                        <Tooltip title={t('admin.edit_listing.btn_delete')}>
                          <Button
                            size="small"
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            style={{ padding: '0 4px', height: 22 }}
                          />
                        </Tooltip>
                      </Popconfirm>
                    </div>
                  </div>
                </Spin>
              ))}
            </div>
          </Image.PreviewGroup>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('admin.edit_listing.empty_no_images')} />
        )}
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        initialValues={{ isNegotiable: true, country: 'Albania' }}
      >
        {/* Vehicle Details */}
        <Divider orientation="left" style={{ color: '#fe5a1d', borderColor: '#fe5a1d' }}>
          {t('admin.edit_listing.section_vehicle_details')}
        </Divider>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="make" label={t('admin.edit_listing.label_make')} rules={[{ required: true }]}>
              <Select
                showSearch
                placeholder={t('admin.edit_listing.ph_select_make')}
                loading={makesLoading}
                optionFilterProp="label"
                onChange={handleMakeChange}
                allowClear
                options={makes.map(m => ({ value: m.make, label: m.make }))}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="model" label={t('admin.edit_listing.label_model')} rules={[{ required: true }]}>
              <Select
                showSearch
                placeholder={t('admin.edit_listing.ph_select_model')}
                loading={modelsLoading}
                optionFilterProp="label"
                allowClear
                disabled={!form.getFieldValue('make')}
                options={models.map(m => ({ value: m.model, label: m.model }))}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="year" label={t('admin.edit_listing.label_year')} rules={[{ required: true }]}>
              <InputNumber min={1900} max={new Date().getFullYear() + 1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="price" label={t('admin.edit_listing.label_price')} rules={[{ required: true }]}>
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                formatter={v => `€ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="mileage" label={t('admin.edit_listing.label_mileage')}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="color" label={t('admin.edit_listing.label_color')}>
              <Select placeholder={t('admin.edit_listing.ph_select_color')} allowClear>
                {CAR_COLORS.map(c => <Option key={c} value={c}>{c}</Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="fuelType" label={t('admin.edit_listing.label_fuel_type')}>
              <Select placeholder={t('admin.edit_listing.ph_select')} allowClear>
                {FUEL_TYPES.map(f => <Option key={f} value={f}>{f}</Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="transmission" label={t('admin.edit_listing.label_transmission')}>
              <Select placeholder={t('admin.edit_listing.ph_select')} allowClear>
                {TRANSMISSIONS.map(tr => <Option key={tr} value={tr}>{tr}</Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="bodyType" label={t('admin.edit_listing.label_body_type')}>
              <Select placeholder={t('admin.edit_listing.ph_select')} allowClear>
                {BODY_TYPES.map(b => <Option key={b} value={b}>{b}</Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="condition" label={t('admin.edit_listing.label_condition')}>
              <Select placeholder={t('admin.edit_listing.ph_select')} allowClear>
                {CONDITIONS.map(c => <Option key={c} value={c}>{c}</Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="doors" label={t('admin.edit_listing.label_doors')}>
              <InputNumber min={1} max={10} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="seats" label={t('admin.edit_listing.label_seats')}>
              <InputNumber min={1} max={20} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="displacement" label={t('admin.edit_listing.label_displacement')}>
              <Input placeholder={t('admin.edit_listing.ph_displacement')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="driveType" label={t('admin.edit_listing.label_drive_type')}>
              <Select placeholder={t('admin.edit_listing.ph_select')} allowClear>
                {DRIVE_TYPES.map(d => <Option key={d.value} value={d.value}>{d.label}</Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="numberOfOwners" label={t('admin.edit_listing.label_num_owners')}>
              <InputNumber min={0} max={20} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="interiorColor" label={t('admin.edit_listing.label_interior_color')}>
              <Select placeholder={t('admin.edit_listing.ph_select')} allowClear>
                {INTERIOR_COLORS.map(c => <Option key={c} value={c}>{c}</Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="isMetallic" label={t('admin.edit_listing.label_metallic')}>
              <Select placeholder={t('admin.edit_listing.ph_select')} allowClear>
                <Option value="yes">{t('admin.edit_listing.yes')}</Option>
                <Option value="no">{t('admin.edit_listing.no')}</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="interiorMaterial" label={t('admin.edit_listing.label_interior_material')}>
              <Select placeholder={t('admin.edit_listing.ph_select')} allowClear>
                {INTERIOR_MATERIALS.map(m => <Option key={m.value} value={m.value}>{m.label}</Option>)}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="title" label={t('admin.edit_listing.label_listing_title')}>
          <Input placeholder={t('admin.edit_listing.ph_title')} />
        </Form.Item>

        <Form.Item name="description" label={t('admin.edit_listing.label_description')}>
          <TextArea rows={4} placeholder={t('admin.edit_listing.ph_description')} />
        </Form.Item>

        <Form.Item name="features" label={t('admin.edit_listing.label_features')}>
          <Select mode="multiple" placeholder={t('admin.edit_listing.ph_select_features')} allowClear>
            {FEATURES_OPTIONS.map(f => <Option key={f} value={f}>{f}</Option>)}
          </Select>
        </Form.Item>

        {/* Location */}
        <Divider orientation="left" style={{ color: '#fe5a1d', borderColor: '#fe5a1d' }}>
          {t('admin.edit_listing.section_location')}
        </Divider>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="location" label={t('admin.edit_listing.label_address')}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="city" label={t('admin.edit_listing.label_city')}>
              <Input placeholder={t('admin.edit_listing.ph_city')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="country" label={t('admin.edit_listing.label_country')}>
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="isNegotiable" label={t('admin.edit_listing.label_negotiable')} valuePropName="checked">
          <Switch />
        </Form.Item>

        {/* Contact */}
        <Divider orientation="left" style={{ color: '#fe5a1d', borderColor: '#fe5a1d' }}>
          {t('admin.edit_listing.section_contact')}
        </Divider>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="contactPhone"
              label={t('admin.edit_listing.label_contact_phone')}
              help={t('admin.edit_listing.help_contact_phone')}
            >
              <Input addonBefore="+355" placeholder={t('admin.edit_listing.ph_contact_phone')} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="displayPhone" label={t('admin.edit_listing.label_display_phone')} valuePropName="checked">
              <Switch checkedChildren={t('admin.edit_listing.yes')} unCheckedChildren={t('admin.edit_listing.no')} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="showWhatsApp" label={t('admin.edit_listing.label_show_whatsapp')} valuePropName="checked">
              <Switch checkedChildren={t('admin.edit_listing.yes')} unCheckedChildren={t('admin.edit_listing.no')} />
            </Form.Item>
          </Col>
        </Row>

        {/* Admin Controls */}
        <Divider orientation="left" style={{ color: '#fe5a1d', borderColor: '#fe5a1d' }}>
          {t('admin.edit_listing.section_admin_controls')}
        </Divider>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="status" label={t('admin.edit_listing.label_status')}>
              <Select placeholder={t('admin.edit_listing.ph_select_status')}>
                {STATUSES.map(s => <Option key={s} value={s}>{s}</Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="adminNotes" label={t('admin.edit_listing.label_admin_notes')}>
              <Input placeholder={t('admin.edit_listing.ph_admin_notes')} />
            </Form.Item>
          </Col>
        </Row>

        <Space style={{ marginTop: 8 }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={saving}
            icon={<SaveOutlined />}
            style={{ background: '#fe5a1d', borderColor: '#fe5a1d' }}
          >
            {t('admin.edit_listing.btn_save')}
          </Button>
          <Button onClick={onClose} disabled={saving}>
            {t('admin.edit_listing.btn_cancel')}
          </Button>
        </Space>
      </Form>
    </Modal>
  );
};

export default AdminEditListingModal;
